import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Edit, Save } from '@mui/icons-material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { legalPageService } from '../services/api';

const LABEL = {
  terms: 'Terms & Conditions',
  privacy: 'Privacy Policy',
  refund: 'Refund and Cancellation Policy',
};

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
};

const EDITOR_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'indent', 'link'];

function plainText(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const LegalPagesPage = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState({ slug: '', title: '', subtitle: '', content: '' });
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  const load = async () => {
    try {
      setLoading(true);
      const res = await legalPageService.getAll();
      const data = res?.data?.data ?? res?.data ?? [];
      setPages(Array.isArray(data) ? data : []);
    } catch (e) {
      setPages([]);
      setToast({ open: true, message: e.response?.data?.error || 'Failed to load pages', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (row) => {
    setEdit({
      slug: row.slug,
      title: row.title || '',
      subtitle: row.subtitle || '',
      content: row.content || '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!edit.title.trim() || !plainText(edit.content)) {
      setToast({ open: true, message: 'Title and content are required', type: 'error' });
      return;
    }
    try {
      setSaving(true);
      await legalPageService.update(edit.slug, {
        title: edit.title.trim(),
        subtitle: edit.subtitle.trim(),
        content: edit.content,
      });
      setOpen(false);
      setToast({ open: true, message: 'Page updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Legal Pages
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Edit Terms &amp; Conditions, Privacy Policy, and Refund and Cancellation Policy. Use the editor to format headings, lists, and links — no HTML required.
        </Typography>

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pages.map((row) => {
              const preview = plainText(row.content);
              return (
                <Card key={row.slug} sx={{ p: 3, border: '1px solid #e8ecf1' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Chip size="small" label={LABEL[row.slug] || row.slug} sx={{ mb: 1, backgroundColor: '#e8f5e9', color: '#2c5f2d' }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{row.title}</Typography>
                      <Typography variant="body2" sx={{ color: '#667085', mb: 1 }}>{row.subtitle}</Typography>
                      <Typography variant="body2" sx={{ color: '#4B4646' }}>
                        {preview.slice(0, 180)}{preview.length > 180 ? '…' : ''}
                      </Typography>
                    </Box>
                    <Button startIcon={<Edit />} onClick={() => startEdit(row)} sx={{ color: '#2c5f2d' }}>
                      Edit
                    </Button>
                  </Box>
                </Card>
              );
            })}
            {pages.length === 0 && (
              <Typography sx={{ color: '#667085', py: 4, textAlign: 'center' }}>
                No legal pages found. Run sql/005_legal_pages.sql on the database, then refresh.
              </Typography>
            )}
          </Box>
        )}

        <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Edit {LABEL[edit.slug] || edit.slug}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Page title"
              value={edit.title}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Short description (shown under the title)"
              value={edit.subtitle}
              onChange={(e) => setEdit({ ...edit, subtitle: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1a202c' }}>
              Page content
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#667085', mb: 1 }}>
              Select text to make it a heading, bold, or a list. Highlight a word and click the link icon to add a URL.
            </Typography>
            <Box
              sx={{
                border: '1px solid #d0d5dd',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                '& .ql-toolbar': {
                  border: 'none',
                  borderBottom: '1px solid #e8ecf1',
                  backgroundColor: '#f8fafb',
                },
                '& .ql-container': {
                  border: 'none',
                  minHeight: 340,
                  fontFamily: '"Poppins", Arial, sans-serif',
                  fontSize: 15,
                },
                '& .ql-editor': {
                  minHeight: 340,
                  color: '#2D2929',
                  lineHeight: 1.7,
                },
                '& .ql-editor.ql-blank::before': {
                  color: '#98a2b3',
                  fontStyle: 'normal',
                },
                '& .ql-snow .ql-stroke': { stroke: '#344054' },
                '& .ql-snow .ql-picker-label': { color: '#344054' },
                '& .ql-active .ql-stroke': { stroke: '#2c5f2d !important' },
                '& .ql-toolbar button:hover': { color: '#2c5f2d' },
              }}
            >
              <ReactQuill
                theme="snow"
                value={edit.content}
                onChange={(content) => setEdit((prev) => ({ ...prev, content }))}
                modules={EDITOR_MODULES}
                formats={EDITOR_FORMATS}
                placeholder="Write the page content here…"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" startIcon={<Save />} disabled={saving} sx={{ backgroundColor: '#2c5f2d' }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default LegalPagesPage;
